<template>
  <main class="announcement-administration">
    <b-card>
      <div class="announcement-administration__heading">
        <div>
          <h1>{{ $t('announcements.title') }}</h1>
          <p class="text-muted mb-0">
            {{ $t('announcements.description') }}
          </p>
        </div>
        <b-button
          variant="primary"
          @click="openCreateModal">
          <v-icon name="plus" />
          {{ $t('announcements.add') }}
        </b-button>
      </div>

      <b-alert
        :show="Boolean(errorMessage)"
        variant="danger">
        {{ errorMessage }}
      </b-alert>

      <div
        v-if="loading"
        class="announcement-administration__state">
        <b-spinner small />
        {{ $t('announcements.loading') }}
      </div>

      <div
        v-else-if="!announcements.length"
        class="announcement-administration__state text-muted">
        {{ $t('announcements.empty') }}
      </div>

      <b-table
        v-else
        :items="announcements"
        :fields="fields"
        responsive
        hover>
        <template #cell(message)="data">
          <span class="announcement-administration__message">{{ data.item.message }}</span>
        </template>
        <template #cell(type)="data">
          <b-badge :variant="data.item.type || 'info'">
            {{ $t(`announcements.types.${data.item.type || 'info'}`) }}
          </b-badge>
        </template>
        <template #cell(period)="data">
          {{ displayPeriod(data.item) }}
        </template>
        <template #cell(enabled)="data">
          <b-badge :variant="data.item.enabled ? 'success' : 'secondary'">
            {{ data.item.enabled ? $t('announcements.published') : $t('announcements.draft') }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            :aria-label="$t('announcements.edit')"
            @click="openEditModal(data.item)">
            <v-icon name="edit" />
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            class="ml-1"
            :aria-label="$t('announcements.delete')"
            @click="confirmDelete(data.item)">
            <v-icon name="trash" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <b-modal
      ref="editorModal"
      :title="editor.id ? $t('announcements.edit') : $t('announcements.add')"
      :ok-title="$t('announcements.save')"
      :cancel-title="$t('announcements.cancel')"
      :ok-disabled="saving"
      @ok="save">
      <b-form @submit.stop.prevent="save">
        <b-alert
          :show="Boolean(editorErrorMessage)"
          variant="danger">
          {{ editorErrorMessage }}
        </b-alert>
        <b-form-group
          :label="$t('announcements.message')"
          label-for="announcement-message">
          <b-form-textarea
            id="announcement-message"
            v-model.trim="editor.message"
            :state="messageState"
            maxlength="1000"
            rows="4"
            required />
          <b-form-invalid-feedback>
            {{ $t('announcements.messageRequired') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          :label="$t('announcements.type')"
          label-for="announcement-type">
          <b-form-select
            id="announcement-type"
            v-model="editor.type"
            :options="typeOptions" />
        </b-form-group>

        <b-form-row>
          <b-col md="5">
            <b-form-group
              :label="$t('announcements.linkLabel')"
              label-for="announcement-link-label">
              <b-form-input
                id="announcement-link-label"
                v-model.trim="editor.linkLabel"
                :state="linkLabelState"
                maxlength="100" />
              <b-form-invalid-feedback>
                {{ $t('announcements.linkLabelRequired') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="7">
            <b-form-group
              :label="$t('announcements.linkUrl')"
              label-for="announcement-link-url">
              <b-form-input
                id="announcement-link-url"
                v-model.trim="editor.linkUrl"
                :state="linkUrlState"
                maxlength="2000"
                placeholder="https://"
                type="url" />
              <b-form-invalid-feedback>
                {{ $t('announcements.linkUrlInvalid') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row>
          <b-col md="6">
            <b-form-group
              :label="$t('announcements.startsAt')"
              label-for="announcement-start-date">
              <b-input-group>
                <b-form-input
                  id="announcement-start-date"
                  v-model="editor.startsAtDate"
                  :aria-label="$t('announcements.date')"
                  type="date" />
                <b-input-group-append>
                  <b-button
                    type="button"
                    variant="outline-secondary"
                    @click="selectNow('startsAt')">
                    {{ $t('announcements.selectNow') }}
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <b-form-input
                id="announcement-start-time"
                v-model="editor.startsAtTime"
                :aria-label="$t('announcements.time')"
                class="mt-2"
                type="time" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('announcements.endsAt')"
              :invalid-feedback="$t('announcements.invalidPeriod')"
              :state="periodState"
              label-for="announcement-end-date">
              <b-input-group>
                <b-form-input
                  id="announcement-end-date"
                  v-model="editor.endsAtDate"
                  :aria-label="$t('announcements.date')"
                  :state="periodState"
                  type="date" />
                <b-input-group-append>
                  <b-button
                    type="button"
                    variant="outline-secondary"
                    @click="selectNow('endsAt')">
                    {{ $t('announcements.selectNow') }}
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <b-form-input
                id="announcement-end-time"
                v-model="editor.endsAtTime"
                :aria-label="$t('announcements.time')"
                :state="periodState"
                class="mt-2"
                type="time" />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-checkbox v-model="editor.enabled">
          {{ $t('announcements.publish') }}
        </b-form-checkbox>
      </b-form>
    </b-modal>
  </main>
</template>

<script>
import {
  createAnnouncement,
  deleteAnnouncement,
  findAllAnnouncements,
  updateAnnouncement
} from '../services/announcementService';

const emptyAnnouncement = () => ({
  id: null,
  message: '',
  type: 'info',
  linkLabel: '',
  linkUrl: '',
  startsAtDate: '',
  startsAtTime: '00:00',
  endsAtDate: '',
  endsAtTime: '00:00',
  enabled: true
});

const toLocalDateTimeParts = value => {
  if (!value) return { date: '', time: '00:00' };

  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  const localDateTime = new Date(date.getTime() - offset).toISOString();
  return {
    date: localDateTime.slice(0, 10),
    time: localDateTime.slice(11, 16)
  };
};

const toLocalDateTime = (date, time) => date ? `${date}T${time || '00:00'}` : '';

export default {
  data() {
    return {
      announcements: [],
      editor: emptyAnnouncement(),
      editorErrorMessage: '',
      errorMessage: '',
      loading: false,
      saving: false,
      submitted: false
    };
  },
  computed: {
    fields() {
      return [
        { key: 'message', label: this.$t('announcements.message') },
        { key: 'type', label: this.$t('announcements.type') },
        { key: 'period', label: this.$t('announcements.period') },
        { key: 'enabled', label: this.$t('announcements.status') },
        { key: 'actions', label: this.$t('announcements.actions'), class: 'text-nowrap' }
      ];
    },
    typeOptions() {
      return [ 'info', 'success', 'warning', 'danger' ].map(value => ({
        value,
        text: this.$t(`announcements.types.${value}`)
      }));
    },
    messageState() {
      if (!this.submitted) return null;
      return Boolean(this.editor.message && this.editor.message.trim());
    },
    linkLabelState() {
      if (!this.submitted || (!this.editor.linkLabel && !this.editor.linkUrl)) return null;
      return Boolean(this.editor.linkLabel);
    },
    linkUrlState() {
      if (!this.submitted || (!this.editor.linkLabel && !this.editor.linkUrl)) return null;
      return this.isValidHttpUrl(this.editor.linkUrl);
    },
    periodState() {
      const startsAt = toLocalDateTime(this.editor.startsAtDate, this.editor.startsAtTime);
      const endsAt = toLocalDateTime(this.editor.endsAtDate, this.editor.endsAtTime);
      if (!startsAt || !endsAt) return null;
      return new Date(endsAt) > new Date(startsAt);
    }
  },
  created() {
    if (!this.$store.getters['security/isAnnouncementAdmin']) {
      this.$router.replace('/404');
      return;
    }

    this.loadAnnouncements();
  },
  methods: {
    loadAnnouncements() {
      this.loading = true;
      this.errorMessage = '';
      findAllAnnouncements()
        .then(announcements => {
          this.announcements = Array.isArray(announcements) ? announcements : [];
        })
        .catch(() => {
          this.errorMessage = this.$t('announcements.loadError');
        })
        .then(() => {
          this.loading = false;
        });
    },
    openCreateModal() {
      this.editor = emptyAnnouncement();
      this.editorErrorMessage = '';
      this.submitted = false;
      this.$refs.editorModal.show();
    },
    openEditModal(announcement) {
      const startsAt = toLocalDateTimeParts(announcement.startsAt);
      const endsAt = toLocalDateTimeParts(announcement.endsAt);
      this.editor = {
        id: announcement.id,
        message: announcement.message,
        type: announcement.type,
        linkLabel: announcement.linkLabel || '',
        linkUrl: announcement.linkUrl || '',
        enabled: announcement.enabled,
        startsAtDate: startsAt.date,
        startsAtTime: startsAt.time,
        endsAtDate: endsAt.date,
        endsAtTime: endsAt.time
      };
      this.editorErrorMessage = '';
      this.submitted = false;
      this.$refs.editorModal.show();
    },
    save(event) {
      if (event && event.preventDefault) event.preventDefault();
      this.submitted = true;

      if (!this.messageState || this.linkLabelState === false || this.linkUrlState === false || this.periodState === false) return;

      this.saving = true;
      this.editorErrorMessage = '';
      const startsAt = toLocalDateTime(this.editor.startsAtDate, this.editor.startsAtTime);
      const endsAt = toLocalDateTime(this.editor.endsAtDate, this.editor.endsAtTime);
      const announcement = {
        message: this.editor.message,
        type: this.editor.type,
        linkLabel: this.editor.linkLabel || null,
        linkUrl: this.editor.linkUrl || null,
        enabled: this.editor.enabled,
        startsAt: startsAt ? new Date(startsAt).toISOString() : null,
        endsAt: endsAt ? new Date(endsAt).toISOString() : null
      };
      const request = this.editor.id
        ? updateAnnouncement(this.editor.id, announcement)
        : createAnnouncement(announcement);

      request
        .then(() => {
          this.$refs.editorModal.hide();
          window.dispatchEvent(new Event('revatech:announcements-changed'));
          this.loadAnnouncements();
        })
        .catch(() => {
          this.editorErrorMessage = this.$t('announcements.saveError');
        })
        .then(() => {
          this.saving = false;
        });
    },
    selectNow(field) {
      const now = toLocalDateTimeParts(new Date().toISOString());
      this.editor[`${field}Date`] = now.date;
      this.editor[`${field}Time`] = now.time;
    },
    isValidHttpUrl(value) {
      if (!value) return false;

      try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch (error) {
        return false;
      }
    },
    confirmDelete(announcement) {
      this.$bvModal.msgBoxConfirm(this.$t('announcements.deleteConfirmation'), {
        title: this.$t('announcements.delete'),
        okTitle: this.$t('announcements.delete'),
        cancelTitle: this.$t('announcements.cancel'),
        okVariant: 'danger',
        centered: true
      }).then(confirmed => {
        if (!confirmed) return;

        deleteAnnouncement(announcement.id)
          .then(() => {
            window.dispatchEvent(new Event('revatech:announcements-changed'));
            this.loadAnnouncements();
          })
          .catch(() => {
            this.errorMessage = this.$t('announcements.deleteError');
          });
      });
    },
    displayPeriod(announcement) {
      if (!announcement.startsAt && !announcement.endsAt) return this.$t('announcements.always');

      const start = announcement.startsAt
        ? new Date(announcement.startsAt).toLocaleString(this.$i18n.locale)
        : this.$t('announcements.now');
      const end = announcement.endsAt
        ? new Date(announcement.endsAt).toLocaleString(this.$i18n.locale)
        : this.$t('announcements.noEnd');
      return `${start} – ${end}`;
    }
  }
};
</script>

<style scoped>
.announcement-administration {
  margin: 0 auto;
  max-width: 1200px;
  padding: 1rem;
}

.announcement-administration__heading {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  text-align: left;
}

.announcement-administration__heading h1 {
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
}

.announcement-administration__message {
  display: block;
  max-width: 520px;
  text-align: left;
  white-space: pre-line;
}

.announcement-administration__state {
  padding: 2rem;
  text-align: center;
}

@media (max-width: 575px) {
  .announcement-administration__heading {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
